import { useState } from 'react'

import CopyIcon from '@/assets/icons/copy.svg?react'

type InlineCopyableProps = {
  value: string | number
  display?: React.ReactNode
  copyText?: string
  onCopy?: (text: string) => void
  disabled?: boolean
  className?: string
  displayClassName?: string
  buttonClassName?: string
  buttonContent?: React.ReactNode
  copiedContent?: React.ReactNode
}

export function InlineCopyable({
  value,
  display,
  copyText,
  onCopy,
  disabled = false,
  className = '',
  displayClassName,
  buttonClassName = '',
  buttonContent,
  copiedContent,
}: InlineCopyableProps) {
  const [copied, setCopied] = useState(false)

  const doCopy = async () => {
    const text = copyText ?? String(value)
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        // fallback
        const ta = document.createElement('textarea')
        ta.value = text
        ta.readOnly = true
        ta.style.position = 'absolute'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCopied(true)
      onCopy?.(text)
      window.setTimeout(() => setCopied(false), 1200)
    } catch {
      console.error('Failed to copy')
    }
  }

  return (
    <span className={`group relative inline-flex items-center gap-2 ${className}`}>
      <span className={displayClassName ?? 't-sm text-primary break-all'}>
        {display ?? String(value)}
      </span>

      <button
        type="button"
        disabled={disabled}
        onClick={doCopy}
        className={[
          'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition',
          't-sm inline-flex items-center gap-1 text-highlight whitespace-nowrap shrink-0',
          disabled && 'opacity-50 cursor-not-allowed',
          buttonClassName,
        ].join(' ')}
        aria-label="Copy"
      >
        {<CopyIcon />}
        {copied ? (copiedContent ?? 'Copied') : (buttonContent ?? 'Copy')}
      </button>
    </span>
  )
}
